export type AdminDeployEnvironment =
  "production" | "preview" | "development" | "local" | "staging" | "unknown";

interface ResolveDeployEnvironmentOptions {
  env?: Partial<ImportMetaEnv> & {
    MODE?: string;
  };
  hostname?: string | undefined;
}

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);
const STAGING_ADMIN_HOSTNAME = "stagingadmin.hellocashflow.com";
const STAGING_ADMIN_HOSTNAMES = new Set([STAGING_ADMIN_HOSTNAME]);

const isLocalPreviewHostname = (hostname: string): boolean =>
  LOCAL_HOSTNAMES.has(hostname) ||
  hostname === "::1" ||
  hostname === "[::1]" ||
  /^127(?:\.\d{1,3}){3}$/u.test(hostname) ||
  hostname.endsWith(".local");

export const isAdminStaticExportHostname = (
  hostnameRaw: string | undefined,
): boolean => {
  const hostname = hostnameRaw?.trim().toLowerCase();
  if (!hostname) {
    return false;
  }

  return (
    STAGING_ADMIN_HOSTNAMES.has(hostname) ||
    hostname.endsWith(".pages.dev") ||
    isLocalPreviewHostname(hostname)
  );
};

export const shouldShowAdminStaticExportLauncher = (): boolean =>
  isAdminStaticExportHostname(
    typeof window === "undefined" ? undefined : window.location.hostname,
  );

const normalizeDeployEnvironment = (
  value: string | undefined,
): AdminDeployEnvironment => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) {
    return "unknown";
  }

  switch (normalized) {
    case "prod":
    case "production":
      return "production";
    case "preview":
      return "preview";
    case "dev":
    case "development":
      return "development";
    case "local":
    case "localhost":
      return "local";
    case "staging":
      return "staging";
    default:
      return "unknown";
  }
};

const resolveEnvironmentFromHostname = (
  hostname: string | undefined,
): AdminDeployEnvironment => {
  const normalizedHost = hostname?.trim().toLowerCase();
  if (!normalizedHost) {
    return "unknown";
  }

  if (STAGING_ADMIN_HOSTNAMES.has(normalizedHost)) {
    return "staging";
  }

  if (normalizedHost.endsWith(".pages.dev")) {
    return "preview";
  }

  if (isLocalPreviewHostname(normalizedHost)) {
    return "local";
  }

  return "unknown";
};

/**
 * Resolves the current admin deploy environment from explicit env vars first,
 * then from the runtime host/mode fallback.
 */
export const resolveAdminDeployEnvironment = (
  options: ResolveDeployEnvironmentOptions = {},
): AdminDeployEnvironment => {
  const env = options.env ?? import.meta.env;

  const explicit = normalizeDeployEnvironment(env.VITE_HC_DEPLOY_ENV);
  if (explicit !== "unknown") {
    return explicit;
  }

  const fromHostname = resolveEnvironmentFromHostname(
    options.hostname ??
      (typeof window !== "undefined" ? window.location.hostname : undefined),
  );
  if (fromHostname !== "unknown") {
    return fromHostname;
  }

  const mode = normalizeDeployEnvironment(env.MODE);
  if (mode !== "unknown") {
    return mode;
  }

  return "production";
};

export const resolveAdminDeployStage = (
  options: ResolveDeployEnvironmentOptions = {},
): string => {
  const deployEnvironment = resolveAdminDeployEnvironment(options);

  switch (deployEnvironment) {
    case "local":
      return "localhost";
    case "production":
    case "preview":
    case "development":
    case "staging":
      return deployEnvironment;
    case "unknown":
      return "production";
  }
};

/**
 * Environment stamped on shipped admin telemetry records.
 *
 * Previously a build-mode constant, so the staging admin portal reported
 * `environment: "production"` and could count toward the production Logz
 * alerts. Development mode stays "development"; staging and preview deploys
 * report themselves; every other stage (production, local preview builds)
 * keeps "production" as before.
 */
export const resolveAdminTelemetryEnvironment = (
  options: ResolveDeployEnvironmentOptions = {},
): "development" | "staging" | "preview" | "production" => {
  const env = options.env ?? import.meta.env;
  if (env.MODE === "development") {
    return "development";
  }
  const deployEnvironment = resolveAdminDeployEnvironment(options);
  return deployEnvironment === "staging" || deployEnvironment === "preview"
    ? deployEnvironment
    : "production";
};

/**
 * Preview email override is required in staging, preview, and local/dev safety environments.
 */
export const isPreviewEmailOverrideEnvironment = (
  options: ResolveDeployEnvironmentOptions = {},
): boolean => {
  const deployEnvironment = resolveAdminDeployEnvironment(options);
  return (
    deployEnvironment === "staging" ||
    deployEnvironment === "preview" ||
    deployEnvironment === "development" ||
    deployEnvironment === "local"
  );
};

/**
 * Environment safety badges are shown in the same non-production environments where we require
 * preview email override.
 */
export const shouldShowEnvironmentSafetyBadges = (
  options: ResolveDeployEnvironmentOptions = {},
): boolean => {
  const hostname =
    options.hostname ??
    (typeof window !== "undefined" ? window.location.hostname : undefined);
  if (hostname?.trim()) {
    // Runtime host is authoritative for customer-visible debug chrome. This
    // prevents stale build variables from exposing badges on a production
    // alias or a future unclassified deployed host.
    return isAdminStaticExportHostname(hostname);
  }

  return isPreviewEmailOverrideEnvironment(options);
};

/**
 * Shows the staging-only header warning on the canonical staging host. A
 * local host may opt into the same warning with an explicit staging deploy
 * environment for local proof; every other host fails closed.
 */
export const shouldShowStagingAdminWarning = (
  options: ResolveDeployEnvironmentOptions = {},
): boolean => {
  const hostname =
    options.hostname ??
    (typeof window === "undefined" ? undefined : window.location.hostname);
  const normalizedHost = hostname?.trim().toLowerCase();

  if (normalizedHost === STAGING_ADMIN_HOSTNAME) {
    return true;
  }

  if (!normalizedHost || !isLocalPreviewHostname(normalizedHost)) {
    return false;
  }

  const env = options.env ?? import.meta.env;
  return normalizeDeployEnvironment(env.VITE_HC_DEPLOY_ENV) === "staging";
};
